import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { MonthLabel } from './MonthLabel';

export interface SingleSelectOption {
  value: string;
  label: string;
  labelSuffix?: string; // Secondary context shown as a small muted parenthetical
  disabled?: boolean;
  tooltip?: string;
}

interface SingleSelectDropdownProps {
  options: SingleSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /**
   * chip   — compact filter chip with an uppercase LABEL: prefix (matches the Tags dropdown).
   * field  — full-width form control (label rendered above via `fieldLabel`).
   * inline — borderless compact trigger (value + chevron) for dense table cells.
   */
  variant?: 'chip' | 'field' | 'inline';
  /** Uppercase prefix shown inside a chip trigger (chip variant). */
  label?: string;
  /** Renders a form label above the control (field variant). */
  fieldLabel?: string;
  required?: boolean;
  helpText?: string;
  error?: string;
  /** Adds a top row that resets the selection to empty. */
  clearable?: boolean;
  clearLabel?: string;
  disabled?: boolean;
  className?: string;
  align?: 'left' | 'right';
}

export function SingleSelectDropdown({
  options,
  value,
  onChange,
  placeholder = 'Select',
  variant = 'field',
  label,
  fieldLabel,
  required,
  helpText,
  error,
  clearable,
  clearLabel,
  disabled,
  className,
  align = 'left',
}: SingleSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, openUp: false });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuH = menuRef.current?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = menuH > 0 && spaceBelow < menuH + 8 && rect.top > spaceBelow;
    const left = align === 'right' ? rect.right : rect.left;
    setPos({ top: openUp ? rect.top - menuH - 4 : rect.bottom + 4, left, width: rect.width, openUp });
  }, [align]);

  // Reposition on open, and on scroll/resize while open
  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Re-measure once the menu has rendered so it can flip upward near the viewport bottom
  useLayoutEffect(() => {
    if (isOpen) updatePosition();
  }, [isOpen, updatePosition]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isOpen]);

  const selected = options.find((o) => o.value === value);
  const displayText = selected
    ? selected.labelSuffix
      ? `${selected.label} (${selected.labelSuffix})`
      : selected.label
    : placeholder;

  const trigger =
    variant === 'chip' ? (
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setIsOpen((o) => !o)}
        disabled={disabled}
        className={`flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md transition-colors min-w-0 max-w-full whitespace-nowrap ${
          disabled
            ? 'bg-gray-50 border-border-default text-text-tertiary cursor-not-allowed'
            : value
            ? 'bg-blue-50 border-blue-300 text-blue-700'
            : 'bg-background-primary border-border-default text-text-primary hover:border-border-focus'
        }`}
      >
        {label && (
          <span className="text-xs font-medium text-text-secondary uppercase flex-shrink-0">{label}:</span>
        )}
        <span className="font-medium truncate max-w-[10rem]" title={displayText}>
          <MonthLabel value={displayText} />
        </span>
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
    ) : variant === 'inline' ? (
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setIsOpen((o) => !o)}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-1 text-xs transition-colors ${
          disabled
            ? 'text-text-tertiary cursor-not-allowed'
            : 'text-text-secondary hover:text-text-primary cursor-pointer'
        }`}
      >
        <span className={`truncate ${selected ? '' : 'text-text-tertiary'}`} title={displayText}>
          <MonthLabel value={displayText} />
        </span>
        <ChevronDownIcon
          className={`w-3.5 h-3.5 flex-shrink-0 text-text-tertiary transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
    ) : (
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setIsOpen((o) => !o)}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border rounded-md transition-colors ${
          disabled
            ? 'bg-gray-50 border-border-default text-text-tertiary cursor-not-allowed'
            : error
            ? 'bg-background-primary border-red-500'
            : 'bg-background-primary border-border-default hover:border-border-focus'
        }`}
      >
        <span className={`truncate ${selected ? 'text-text-primary' : 'text-text-tertiary'}`} title={displayText}>
          <MonthLabel value={displayText} />
        </span>
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform flex-shrink-0 text-text-tertiary ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
    );

  const menu =
    isOpen &&
    createPortal(
      <div
        ref={menuRef}
        className="bg-background-elevated border border-border-default rounded-md shadow-lg"
        style={{
          position: 'fixed',
          top: pos.top,
          left: align === 'right' ? undefined : pos.left,
          right: align === 'right' ? window.innerWidth - pos.left : undefined,
          minWidth: pos.width,
          maxWidth: 320,
          zIndex: 9999,
        }}
      >
        <div className="max-h-64 overflow-y-auto overflow-x-hidden rounded-md">
          {clearable && (
            <>
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setIsOpen(false);
                }}
                className="w-full flex items-center justify-between gap-3 px-3 py-2 text-sm text-left hover:bg-surface-hover"
              >
                <span className="text-text-tertiary">{clearLabel ?? placeholder}</span>
                {!value && <CheckIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />}
              </button>
              <div className="border-t border-border-subtle" />
            </>
          )}

          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  if (option.disabled) return;
                  onChange(option.value);
                  setIsOpen(false);
                }}
                disabled={option.disabled}
                title={option.tooltip ?? (option.labelSuffix ? `${option.label} (${option.labelSuffix})` : option.label)}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-sm text-left ${
                  option.disabled
                    ? 'opacity-40 cursor-not-allowed'
                    : isSelected
                    ? 'bg-blue-50 text-blue-700 hover:bg-blue-50'
                    : 'hover:bg-surface-hover'
                }`}
              >
                <span className={`flex-1 min-w-0 truncate ${option.disabled ? 'text-text-tertiary' : ''}`}>
                  <MonthLabel value={option.label} />
                  {option.labelSuffix && (
                    <span className="text-xs italic text-text-tertiary"> ({option.labelSuffix})</span>
                  )}
                </span>
                {isSelected && <CheckIcon className="w-4 h-4 flex-shrink-0" />}
              </button>
            );
          })}

          {options.length === 0 && (
            <div className="px-3 py-4 text-sm text-text-secondary text-center">No options available</div>
          )}
        </div>
      </div>,
      document.body,
    );

  return (
    <div className={`${variant === 'field' ? 'space-y-1.5' : ''} ${className ?? ''}`}>
      {fieldLabel && (
        <label className="block text-sm font-medium text-text-primary">
          {fieldLabel}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative min-w-0">
        {trigger}
        {menu}
      </div>
      {error ? (
        <p className="text-sm text-red-500">{error}</p>
      ) : (
        helpText && <p className="text-sm text-text-secondary">{helpText}</p>
      )}
    </div>
  );
}
